import { forwardRef, useRef } from "react"
import useTranslation from "../../hooks/useTranslation"
import { FormGroup } from "../form/FormComponents"

//Filed Level Components
import CountrySelect from '../country/CountrySelect'
import Select from "../select/Select"
import Currency from "../currency/Currency"
import Connect from "../select/Connect"
import CardSelect from "../select/CardSelect"
import HConnect from "../select/HConnect"
import DualConnect from "../select/DualConnect"
import DataList from "../dataList/DataList"
import DateInput from "../calendar/DateInput"
import DateTimeInput from "../dateTime/DateTimeInput"
import NumericInput from "../numeric/NumericInput"
import DualInput from "../formElem/DualInput"
import NSerieInput from "../nSerie/NSerieInput"
import { Separator } from "../generic/Core"
import Upload from "../upload/Upload"
import GroupFormElem from "./GroupFormElem"
import ReadOnly from "../generic/ReadOnly"
import PdfPager from "../pdf/PdfPager"
import TimeStamp from "../generic/TimeStamp"
import ModalFormElem from "./ModalFormElem"

import Description from "../description/Description"
import SwitchButton from "../generic/SwitchButton"
import SwitchSelect from "../select/SwitchSelect"
import runFieldsLogic from "../../../../common/logic/runFieldsLogic"
import extractNames from "../../utils/msg/extractNames"
import sanitize from "../../../../common/utils/string/sanitize"
import Hidden from "../formElem/Hidden"
import FileUpload from "../formElem/FileUpload"
import ChipInput from "../formElem/ChipInput"
import JsonInput from "../formElem/JsonInput"
import CompanyLookup from "../formElem/CompanyLookup"
import VatChecker from "../formElem/VatChecker"
import RichText from "../richText/RichText"
import WeekDayPicker from "../calendar/WeekDayPicker"
import ArrayModal from "../generic/ArrayModal"
import StateBadge from "../generic/StateBadge"

import { getNestedValueKey, setNestedValue } from "../../utils/objects/nestedValue"
import { conditionFields, resolveVisibility, evaluateCondition } from "../../../../common/model/condition/evaluate"
import useConditionValues from "./useConditionValues"
import { resolveElement, conditionalFieldsOf } from "../../../../common/model/condition/resolveElement"

/*
 * Elements that present something rather than collect it — a heading, a rule, a
 * value the user cannot set. `dimUntouched` never applies to them: the muting
 * means "not part of what will be written", and that is only a statement worth
 * making about a field someone can fill in.
 */
const STATIC_TYPES = new Set([
    'description', 'separator', 'stateBadge', 'text-readOnly', 'timestamp', 'hidden', 'pdf'
])

const FormElem = forwardRef(({ item, lineId, validated, section, entity, useStore, path, options, origin }, ref) => {

    //Setting up all variables
    let fieldValue, fieldDisplay, fieldParam, setState, localState, lineFieldValue, linkedFields, fieldSubDisplay, fieldId, fieldSubId, fieldCondition, itemCondition, secondaryValue

    let showElem = true

    // Visibility is resolved once, in common/model/condition — see §7.3 of
    // 30.03.02.E for why this used to live in five places that disagreed.


    const { t } = useTranslation()

    // Current record id — lets a connect field read a subcollection of the
    // record via an entity template like 'warehouse/:id/hall'.
    const [recordId] = useStore((s) => s.id)

    const sectionName = section?.id
    const linesArray = section?.meta?.linesArray
    const subObject = item.subObject

    // if (subObject) (`<FormElem> for ${linesArray} of item ${subObject}.${item.id} path: `, path) 
    //     else console.log(`<FormElem> for ${linesArray} of item ${item.id} path: `, path)


    //Setting up the main state for NON Lines.
    if (lineId === undefined) {
        if (subObject) {
            [fieldValue, setState] = useStore((s) => s[subObject]?.[item.id])
        } else {
            [fieldValue, setState] = useStore((s) => s[item.id])
        }
    }


    // Subscribe to exactly the fields the condition reads. Called unconditionally
    // - useStore defaults to shallowEqual and caches the reference, so a fresh
    // object per render costs nothing and keeps the hook order stable.
    const conditionKeys = [...new Set([
        ...conditionFields(item?.condition),
        ...conditionFields(item?.highlight?.condition),
        ...conditionalFieldsOf(item),
        // emptyActions pills: their own conditions, and the values they send
        ...(item?.emptyActions || []).flatMap(a => [...conditionFields(a.condition), ...(a.params || [])]),
    ])]
    const conditionValues = useConditionValues(useStore, conditionKeys, lineId, path, subObject)

    // Hook COUNT must depend only on static config. `conditionals` can `unset` a
    // property that gates a hook (`parameter`, `linkedFields`), and their
    // predicates can read form fields the user edits — so the subscriptions below
    // key off `baseItem`, which never changes, while behaviour uses the resolved
    // element. Subscribing to a field the resolved element ignores is harmless.
    const baseItem = item
    item = resolveElement(item, { values: conditionValues, context: { mode: options?.mode } })

    // Generic value-vs-value highlight: any field can declare
    // `highlight: { condition }` and turn red when that condition holds — most
    // often a comparison against a sibling field via `valueFrom: 'values.other'`
    // (e.g. totalDebitBase highlighting when it's lower than totalCreditBase).
    const isHighlighted = item.highlight ? evaluateCondition(item.highlight.condition, { values: conditionValues, context: { mode: options?.mode } }) : false

    // A connect field's emptyActions pills, those whose condition holds now.
    const emptyActions = (item.emptyActions || []).filter(a =>
        !a.condition || evaluateCondition(a.condition, { values: conditionValues, context: { mode: options?.mode } }))

    //Conditional State depending on type of element
    //If the element has a display value retrieve the state for that
    // Always subscribed, whatever the resolved element says: `conditionals` can
    // add a displayField (a text box that becomes a picker — Studio's model
    // key), and a hook that appears mid-mount crashes React. Only `lineId`,
    // fixed for an element's life, decides whether this runs.
    if (lineId === undefined) {
        const displayKey = item.displayField
        if (subObject) {
            [fieldDisplay] = useStore((s) => (displayKey ? s[subObject]?.[displayKey] : undefined))
        } else {
            [fieldDisplay] = useStore((s) => (displayKey ? s[displayKey] : undefined))
        }
    }
    if (item.sub?.displayField && lineId === undefined) [fieldSubDisplay] = useStore((s) => s[item.sub?.displayField])

    if (item.id && lineId === undefined) [fieldId] = useStore((s) => s[item.id])
    if (item.sub?.id && lineId === undefined) [fieldSubId] = useStore((s) => s[item.sub?.id])
    if (item.secondaryId && lineId === undefined) [secondaryValue] = useStore((s) => s[item.secondaryId])

    if (item.showSubField && lineId === undefined) [fieldCondition] = useStore((s) => s[item.showSubField.field])
    ;[fieldParam] = useStore((s) => (baseItem.parameter ? s[baseItem.parameter] : undefined))

    let actionConditionValue
    if (item.action?.pathField && lineId === undefined) {
        [actionConditionValue] = useStore((s) => s[item.action.pathField])
    }


    //if the element has a Line retrieve the Line Value and a corresponding diplay valus if the field has that.
    if (lineId !== undefined) {
        if (subObject) {
            [lineFieldValue, setState] = useStore((s) => getNestedValueKey(s, path, item.id, subObject))
            // Always subscribed; see the displayField note above.
            [fieldDisplay] = useStore((s) => (item.displayField ? getNestedValueKey(s, path, item.displayField, subObject) : undefined))
            if (item.id) [fieldId] = useStore((s) => getNestedValueKey(s, path, item.id, subObject))
            if (item.sub?.id) [fieldSubId] = useStore((s) => getNestedValueKey(s, path, item.sub?.id, subObject))
            if (item.sub?.displayField) [fieldSubDisplay] = useStore((s) => getNestedValueKey(s, path, item.sub?.displayField, subObject))
        } else {
            [lineFieldValue, setState] = useStore((s) => getNestedValueKey(s, path, item.id))
            [fieldDisplay] = useStore((s) => (item.displayField ? getNestedValueKey(s, path, item.displayField) : undefined))
            if (item.id) [fieldId] = useStore((s) => getNestedValueKey(s, path, item.id))
            if (item.sub?.id) [fieldSubId] = useStore((s) => getNestedValueKey(s, path, item.sub?.id))
            if (item.sub?.displayField) [fieldSubDisplay] = useStore((s) => getNestedValueKey(s, path, item.sub?.displayField))
            if (item.secondaryId) [secondaryValue] = useStore((s) => getNestedValueKey(s, path, item.secondaryId))
        }
        if (item.showSubField && lineId !== undefined) {
            [fieldCondition] = useStore((s) => getNestedValueKey(s, path, item.showSubField.field))
        }
    }

    if (baseItem.linkedFields) {
        linkedFields = {};
        baseItem.linkedFields.forEach(f => {
            const [v] = (useStore(s => s[f]));
            linkedFields[f] = v
        });
    }

    if (baseItem.lineLinkedFields) {
        if (!linkedFields) linkedFields = {};
        baseItem.lineLinkedFields.forEach(f => {
            const [v] = useStore(s => (lineId !== undefined && path) ? getNestedValueKey(s, path, f) : undefined);
            if (v !== undefined) linkedFields[f] = v;
        });
    }

    //Managing the state
    if (lineId !== undefined) {
        localState = lineFieldValue
    } else {
        localState = fieldValue
    }

    const handleInput = async (msg) => {
        if (msg === null) return;
        let stateValue = {}

        // Check if msg is already in the correct format (from Connect component)
        if (msg[subObject]) {
            stateValue = msg
        } else if (Array.isArray(msg)) {
            msg.forEach(
                (elem) => {
                    const target = elem.target
                    const name = target.name
                    const value = target.value
                    //Transforms strings in numbers
                    stateValue = { ...stateValue, ...{ [name]: value } }
                })
        } else if (msg.target) {
            const target = msg.target
            const name = target.name
            const value = target.value
            //Transforms strings in numbers
            stateValue = { [name]: value }
        } else {
            // Direct value object
            stateValue = msg
        }

        if (lineId !== undefined) {
            if (subObject && !stateValue[subObject]) {
                stateValue = { [subObject]: stateValue }
            }
            // Pass path in options to setState
            setState(stateValue, { path: path || [] });
            return;
        } else {
            if (item.linkedFields) {
                const fields = extractNames(msg)
                let prevData = { ...linkedFields }
                let data = { ...linkedFields, ...stateValue }
                const processedValues = await runFieldsLogic(data, entity, fields, prevData)
                //console.log('[i] processedValues: ', processedValues)
                stateValue = processedValues
            }
            if (subObject && !stateValue[subObject]) {
                stateValue = { [subObject]: stateValue }
            }
            setState(stateValue)
        }
    }

    const handleBlur = (e) => {
        const { name, value } = e.target;

        // Only sanitize if it’s a code field
        if (item.type === "code" && value.length > 0) {
            const sanitized = sanitize(value);

            // If the sanitized version differs, update both state and the DOM input
            if (sanitized !== value) {
                setState({ [name]: sanitized });
                e.target.value = sanitized; // visually updates the field itself
            }
        }
    };

    //Managing the Autocomplete Value
    const autoCompleteMap = { email: 'email', tel: 'tel', url: 'url', password: 'current-password' }
    const autoCompleteValue = item.autoComplete ?? autoCompleteMap[item.type] ?? 'off'

    // One resolver, one answer. `$mode` is render context, not form state.
    // FormElem always hides the CONTENT. Whether the layout cell survives is
    // ElemToShow's call, so 'reserve' hides here too and the wrapper stays.
    const visibility = resolveVisibility(item, {
        values: conditionValues,
        context: { mode: options?.mode },
    })
    if (visibility !== 'show') showElem = false

    //Setting up the TestId
    let testId = `${sectionName}.${item.id}`
    if (lineId !== undefined) { testId = `${sectionName}.${lineId + 1}.${item.id}` }

    const elemId = lineId !== undefined ? `${item.id}-${lineId}` : item.id

    /*
     * A partial-update form — the bulk "update selected records" modals — writes
     * only the fields the user actually set, and leaves every other field alone
     * on every selected record. `dimUntouched` on the section says that on
     * screen: a field the store has no value for is not part of the write, so it
     * stays muted until it is.
     *
     * Absence is the test, not emptiness. Clearing a field back to '' is a
     * deliberate edit that does get written, so it reads as touched.
     */
    const untouched = Boolean(section?.meta?.dimUntouched)
        && !STATIC_TYPES.has(item.type)
        && lineId === undefined
        && fieldValue === undefined

    return (
        showElem && <>
            <FormGroup name='FormGroup' $ctrlid={item.id} $margin={item.fgMargin} $untouched={untouched} key={item.id} >
                {/*  Label  */}
                {item.label ? <FormGroup.Label as={['select', 'dualconnect', 'arrayModal', 'switchSelect', 'currency'].includes(item.type) ? 'span' : undefined} id={['select', 'dualconnect', 'arrayModal', 'switchSelect', 'currency'].includes(item.type) ? `label-${elemId}` : undefined} htmlFor={!['switchSelect', 'select', 'dualconnect', 'arrayModal', 'currency'].includes(item.type) ? elemId : undefined}>{t(item.label)}</FormGroup.Label>
                : item.invisibleLabel ? <FormGroup.Label as='span' aria-hidden='true' style={{ visibility: 'hidden' }}>&nbsp;</FormGroup.Label>
                : <></>}

                {/*  Regular Text Input Field  */}
                {item.id && item.type === 'text' ? <FormGroup.Control type={item.type} id={elemId} name={item.id}
                    value={localState || ''} onChange={handleInput} placeholder={t(item.placeholder)}
                    autoComplete={autoCompleteValue}
                    {...(validated && item.required && { required: true })}
                    {...(ref && { ref })} data-testid={testId}
                /> : <></>}

                {/*  Text field that also looks the company up in its registry (task 39.01)  */}
                {item.id && item.type === 'companyLookup' ? <CompanyLookup name={item.id} inputId={elemId}
                    value={localState} setValue={handleInput} placeholder={t(item.placeholder)}
                    country={fieldParam} valueMap={item.valueMap}
                    required={validated && item.required} inputRef={ref} testId={testId}
                /> : <></>}

                {/*  EU VAT ID with its VIES check; the result lives in `${id}Status` etc.  */}
                {item.id && item.type === 'vatChecker' && lineId === undefined ? <VatChecker name={item.id} inputId={elemId}
                    value={localState} setValue={handleInput} placeholder={t(item.placeholder)} useStore={useStore}
                    valueMap={item.valueMap} required={validated && item.required} inputRef={ref} testId={testId}
                /> : <></>}

                {/*  Regular Text Input Field  */}
                {item.id && item.type === 'code' ? <FormGroup.Control type='text' id={elemId} name={item.id}
                    value={localState || ''} onChange={handleInput} onBlur={handleBlur}
                    placeholder={t(item.placeholder)}
                    autoComplete={autoCompleteValue} maxLength={item.maxLen || 3} uppercase={item.uppercase}
                    {...(item.lockOnEdit && recordId ? { disabled: true } : {})}
                    {...(validated && item.required && { required: true })}
                    {...(ref && { ref })} data-testid={testId}
                /> : <></>}

                {/*  Email Field  */}
                {item.id && item.type === 'email' ? <FormGroup.Control type={item.type} id={elemId} name={item.id}
                    value={localState || ''} onChange={handleInput} placeholder={t(item.placeholder)}
                    autoComplete={autoCompleteValue} inputMode='email'
                    {...(validated && item.required && { required: true })}
                    {...(ref && { ref })} data-testid={testId}
                /> : <></>}

                {/*  URL Field  */}
                {item.id && item.type === 'url' ? <FormGroup.Control type={item.type} id={elemId} name={item.id}
                    value={localState || ''} onChange={handleInput} placeholder={t(item.placeholder)}
                    autoComplete={autoCompleteValue} inputMode='url'
                    {...(validated && item.required && { required: true })}
                    {...(ref && { ref })} data-testid={testId}
                /> : <></>}

                {/*  search  */}
                {item.id && item.type === 'search' ? <FormGroup.Control type={item.type} id={elemId} name={item.id}
                    value={localState || ''} onChange={handleInput} placeholder={t(item.placeholder)}
                    autoComplete={autoCompleteValue} inputMode='search'
                    {...(validated && item.required && { required: true })}
                    {...(ref && { ref })} data-testid={testId}
                /> : <></>}

                {/*  phone  */}
                {item.id && item.type === 'tel' ? <FormGroup.Control type={item.type} id={elemId} name={item.id} value={localState || ''} onChange={handleInput} placeholder={t(item.placeholder)} autoComplete={autoCompleteValue} inputMode='tel'
                    {...(validated && item.required && { required: true })}
                    {...(ref && { ref })} data-testid={testId}
                /> : <></>}

                {/*   Text Area Input Field  */}
                {item.id && item.type === 'textarea' ? <FormGroup.TextArea
                    $rows={item.rows}
                    id={elemId} name={item.id} value={localState || ''} onChange={handleInput}
                    placeholder={t(item.placeholder)} autoComplete={autoCompleteValue}
                    {...(validated && item.required && { required: true })}
                    {...(ref && { ref })} data-testid={testId}
                /> : <></>}

                {/*  JSON editor — Studio's overlay patches (30.03.08)  */}
                {item.id && item.type === 'json' ? <JsonInput
                    name={item.id} value={localState} setValue={handleInput}
                    rows={item.rows} readOnly={item.readOnly === true}
                    placeholder={t(item.placeholder)}
                    inputId={elemId} testId={testId}
                    isRed={Boolean(validated && item.required && !localState)}
                /> : <></>}

                {/*  Number Input Field  */}
                {item.id && item.type === 'number' ? <NumericInput type={item.type} $variant={item.variant} name={item.id} inputId={elemId} value={localState || 0} setValue={handleInput} placeholder={t(item.placeholder)} decimals={item.decimals}
                    {...((validated && item.required) ? { isRed: true } : { isRed: false })}
                    {...(ref && { ref })} testId={testId}
                /> : <></>}

                {/*  Dual Input Field — one value, one of two fields, picked by a switch  */}
                {item.id && item.type === 'dualInput' ? <DualInput
                    name={item.id} secondaryName={item.secondaryId}
                    primaryValue={localState || 0} secondaryValue={secondaryValue || 0}
                    setValue={handleInput}
                    placeholder={item.placeholder}
                    primaryLabel={item.primaryLabel} secondaryLabel={item.secondaryLabel}
                    $variant={item.variant} decimals={item.decimals}
                    linkedFields={linkedFields} defaultSide={item.defaultSide}
                    inputId={elemId} testId={testId}
                    {...((validated && item.required) ? { isRed: true } : { isRed: false })}
                /> : <></>}

                {/*  Number Serie Input Field  */}
                {item.id && item.type === 'nserie' ? <NSerieInput item={item} name={item.id} inputId={elemId} value={localState || ''} setValue={handleInput} placeholder={t(item.placeholder)} entity={item.entity}
                    {...((validated && item.required) ? { isRed: true } : { isRed: false })}
                    {...(ref && { ref })} testId={testId}
                /> : <></>}

                {/*  Password Input Field  */}
                {item.id && item.type === 'password' ? <FormGroup.Control type={item.type} id={elemId} name={item.id} value={localState || ''} onChange={handleInput} placeholder={t(item.placeholder)} autoComplete={autoCompleteValue}
                    {...(validated && item.required && { required: true })}
                    {...(ref && { ref })} data-testid={testId}
                /> : <></>}

                {/*  Read-Only Text Input Field  */}
                {/*            {item.id && item.type === 'text-readOnly' ? <FormGroup.Control displayType={item.displayType} type='text' name={item.id} value={localState || ''} onChange={handleInput} placeholder={t(item.placeholder)} className={'readonly'} readOnly={true}
                {...(ref && { ref })}
            /> : <></>} */}
                {item.id && item.type === 'text-readOnly' ? <ReadOnly displayType={item.displayType}
                    type='text' name={item.id} value={localState || ''} fieldId={fieldId} onChange={handleInput}
                    placeholder={t(item.placeholder)} className={'readonly'} readOnly={true}
                    action={item.action?.pathMap
                        ? { ...item.action, path: item.action.pathMap[actionConditionValue] }
                        : item.action}
                    highlight={isHighlighted}
                    testId={testId} rows={item.rows || null}
                    {...(ref && { ref })}
                /> : <></>}

                {/*  State Badge  */}
                {item.id && item.type === 'stateBadge' ? <StateBadge value={localState} states={item.states} /> : <></>}

                {/*  Country Selector  */}
                {item.id && item.type === 'country' ? <CountrySelect name={item.id} valueName={fieldDisplay || ''} valueId={localState || ''} valueMap={item.valueMap} setValue={handleInput}
                    {...((validated && item.required) ? { isRed: true } : { isRed: false })}
                    {...(ref && { ref })} testId={testId}
                /> : <></>}

                {/*  Generic Select Control  */}
                {item.id && item.type === 'select' ? <Select entity={item.entity} name={item.id}
                    valueName={fieldDisplay || ''} valueId={localState || ''} valueMap={item.valueMap} setValue={handleInput}
                    options={item.options} nullDefaults={item.nullDefaults || {}} defaultType={item.defaultType}
                    configDefault={item.defaultValue} labelId={item.label ? `label-${elemId}` : undefined}
                    inputId={elemId} entityParams={item.entityParams} labelLanguage={item.labelLanguage} linkedFields={linkedFields}
                    {...((validated && item.required) ? { isRed: true } : { isRed: false })}
                    {...(ref && { ref })} testId={testId}
                /> : <></>}

                {/*  Currency Control  */}
                {item.id && item.type === 'currency' ? <Currency entity={item.entity} name={item.id}
                    valueName={fieldDisplay || ''} valueId={localState || ''} valueMap={item.valueMap} setValue={handleInput}
                    options={item.options} parameter={fieldParam || ''}
                    {...((validated && item.required) ? { isRed: true } : { isRed: false })}
                    {...(ref && { ref })} testId={testId}
                /> : <></>}

                {/*  Data List Control  */}
                {item.id && item.type === 'dataList' ? <DataList entity={item.entity} name={item.id}
                    valueName={fieldDisplay || ''} valueMap={item.valueMap} setValue={handleInput}
                    options={item.options} modalTitle={item.modalTitle} modalOpacity={item.modalOpacity}
                    {...((validated && item.required) ? { isRed: true } : { isRed: false })}
                    {...(ref && { ref })} testId={testId}
                /> : <></>}

                {/*  Generic Connect Control  */}
                {item.id && item.type === 'connect' ? <Connect entity={item.entity} parentId={recordId} name={item.id}
                    id={item.id} mainValueId={fieldId || ''} linkedFields={linkedFields}
                    valueName={fieldDisplay || ''} valueMap={item.valueMap} setValue={handleInput}
                    options={item.options} modalTitle={item.modalTitle} placeholder={item.placeholder}
                    displayField={item.displayField}
                    menuItemField={item.menuItemField || 'name'} listDisplay={item.listDisplay}
                    inline={item.inline || false}
                    modalType={item.modalType || 'searchList'}
                    radioGroups={item.radioGroups || []} subObject={subObject} persistValue={item.persistValue || false} selectButtonKey={item.selectButtonKey}
                    defaultRadioGroups={item.defaultRadioGroups}
                    manualEntry={item.manualEntry || false}
                    inputId={elemId}
                    conditionalFields={item.conditionalFields}
                    nullDefaults={item.nullDefaults || {}}
                    emptyActions={emptyActions} actionValues={conditionValues}
                    {...((validated && item.required) ? { isRed: true } : { isRed: false })}
                    {...(ref && { ref })} testId={testId} entityParams={item.entityParams}
                /> : <></>}

                {/*  Card Select Control — the short-list picker, configured by name  */}
                {item.id && item.type === 'cardSelect' ? <CardSelect
                    configRef={item.cardSelectRef}
                    valueId={fieldId || ''}
                    setValue={handleInput}
                    size={item.size}
                    entityParams={item.entityParams}
                    linkedFields={linkedFields}
                    {...((validated && item.required) ? { isRed: true } : { isRed: false })}
                    testId={testId}
                /> : <></>}

                {/*  HConnect Control  */}
                {item.id && item.type === 'hconnect' ? <HConnect
                    entity={item.entity}
                    name={item.id}
                    id={item.id}
                    mainValueId={fieldId || ''}
                    linkedFields={linkedFields}
                    valueName={fieldDisplay || ''}
                    valueMap={item.valueMap}
                    setValue={handleInput}
                    options={item.options}
                    modalTitle={item.modalTitle}
                    placeholder={item.placeholder}
                    parentId={item.parentId}
                    parentField={item.parentField}
                    subObject={subObject}
                    persistValue={item.persistValue || false}
                    inputId={elemId}
                    {...((validated && item.required) ? { isRed: true } : { isRed: false })}
                    {...(ref && { ref })}
                    testId={testId}
                    entityParams={item.entityParams}
                /> : <></>}

                {/*  DualConnect Control  */}
                {item.id && item.type === 'dualconnect' ? <DualConnect
                    mainEntity={item.entity}
                    subEntity={item.sub?.entity}
                    id={item.id}
                    subId={item.sub?.id}
                    mainValueName={fieldDisplay || ''}
                    mainValueId={fieldId || ''}
                    subValueId={fieldSubId || ''}
                    subValueName={fieldSubDisplay || ''}
                    mainValueMap={item.valueMap}
                    mainNullDefaults={item.nullDefaults || {}}
                    subValueMap={item.sub?.valueMap}
                    setMainValue={handleInput}
                    setSubValue={handleInput}
                    options={item.options}
                    isRed={validated && item.required}
                    mainModalTitle={item.modalTitle}
                    subModalTitle={item.sub?.modalTitle}
                    mainPlaceholder={t(item.placeholder)}
                    subPlaceholder={t(item.sub?.placeholder)}
                    showSubField={item?.showSubField?.value === '*' || fieldCondition == item?.showSubField?.value}
                    menuItemField={item.menuItemField || 'name'}
                    mainListDisplay={item.listDisplay}
                    subListDisplay={item.sub?.listDisplay}
                    persistMainValue={item.persistMainValue || false}
                    persistSubValue={item.persistSubValue || false}
                    mainEntityParams={item.entityParams}
                    labelId={item.label ? `label-${elemId}` : undefined}
                    inputId={elemId}
                    {...(ref && { ref })} testId={testId}
                /> : <></>}

                {/*  Date Control  */}
                {item.id && item.type === 'date' ? <DateInput type={item.type} id={elemId} name={item.id} value={localState || ''} setValue={handleInput} placeholder={t(item.placeholder)}
                    {...(validated && item.required && { required: true })}
                    {...(ref && { ref })} align={item.align || 'right'} data-testid={testId} testId={testId}
                /> : <></>}

                {/*  DateTime Control  */}
                {item.id && item.type === 'datetime' ? <DateTimeInput id={elemId} name={item.id} value={localState || ''} setValue={handleInput}
                    {...(validated && item.required && { required: true })}
                    {...(ref && { ref })} align={item.align || 'right'} testId={testId}
                    readOnly={item.readOnly || false}
                /> : <></>}

                {/*  Boolean Switch Control  */}
                {item.id && item.type === 'switch' ? <SwitchButton name={item.id} value={localState || ''} setValue={handleInput} switchText={t(item.switchText)}
                    {...(ref && { ref })} testId={testId}
                /> : <></>}

                {/*  Switch Select Control  */}
                {item.id && item.type === 'switchSelect' ? <SwitchSelect
                    entity={item.entity}
                    valueName={fieldDisplay || ''}
                    valueId={localState || ''}
                    setValue={handleInput}
                    options={item.options || {}}
                    valueMap={item.valueMap}
                    nullDefaults={item.nullDefaults || {}}
                    placeholder={t(item.placeholder)}
                    readOnly={item.readOnly || false}
                    {...((validated && item.required) ? { isRed: true } : { isRed: false })}
                    {...(ref && { ref })}
                    testId={testId}
                /> : <></>}

                {/*  Text Line  */}
                {item.type === 'description' ? <Description item={item} testId={testId} linkedFields={linkedFields || []}
                ></Description> : <></>}

                {/*  separator  */}
                {item.type === 'separator' ? <Separator /> : <></>}

                {/*  Upload  */}
                {item.type === 'upload' ? <Upload value={localState || ''} onChange={handleInput}
                    dataItems={{ ...item }} data-testid={testId}
                /> : <></>}

                {/*  PDF  */}
                {item.type === 'pdf' ? <PdfPager fData={fieldValue} fState={fieldValue}
                /> : <></>}

                {/*  TimeStamp Control  */}
                {item.id && item.type === 'timestamp' ? <TimeStamp name={item.id} setValue={handleInput} testId={testId} /> : <></>}

                {/*  Group  */}
                {item.type === 'group' ? <GroupFormElem item={item} lineId={lineId} validated={validated} section={section} entity={entity} useStore={useStore} ref={ref} /> : <></>}

                {item.type === 'modal' ? <ModalFormElem
                    lineId={lineId}
                    item={item}
                    validated={validated}
                    section={section}
                    entity={entity}
                    useStore={useStore}
                    path={path}
                    options={options}
                    origin={origin}
                    subObject={subObject}
                /> : <></>
                }

                {item.type === 'hidden' ? <Hidden lineId={lineId} localState={localState} id={item.id} useStore={useStore} statePath={item.statePath} path={path} onChange={handleInput} /> : <></>}

                {/*  Array Modal — editable flat array of objects in a popup table  */}
                {item.type === 'arrayModal' ? <ArrayModal
                    id={item.id}
                    value={localState}
                    onChange={handleInput}
                    modalTitle={item.modalTitle}
                    addLabel={item.addLabel}
                    displayField={item.displayField}
                    columns={item.columns || []}
                /> : <></>}

                {/*  WeekDay Picker  */}
                {item.id && item.type === 'weekday' ? <WeekDayPicker
                    name={item.id}
                    value={localState}
                    setValue={handleInput}
                    testId={testId}
                /> : <></>}

                {/*  RichText Editor  */}
                {item.id && item.type === 'richtext' ? <RichText
                    name={item.id}
                    value={localState || ''}
                    setValue={handleInput}
                    placeholder={t(item.placeholder)}
                    rows={item.rows}
                    resizable={item.resizable}
                    {...((validated && item.required) ? { isRed: true } : { isRed: false })}
                    testId={testId}
                /> : <></>}

                {item.type === 'fileUpload' ? <FileUpload
                    name={item.field || item.id}
                    fileNameField={item.fileNameField}
                    fileSizeField={item.fileSizeField}
                    folder={item.folder}
                    accept={item.accept}
                    label={item.label}
                    value={localState || ''}
                    fileName={item.fileNameField ? undefined : undefined}
                    onChange={handleInput}
                /> : <></>}

                {item.id && item.type === 'chipInput' ? <ChipInput
                    name={item.id}
                    value={localState}
                    onChange={handleInput}
                    placeholder={item.placeholder}
                    testId={testId}
                /> : <></>}

            </FormGroup>
        </>
    )
})

export default FormElem
